import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(localStorage.getItem('session_token'));

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // Check for session_id in URL fragment (from Google OAuth)
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleOAuthCallback(sessionId);
    }
  }, []);

  const handleOAuthCallback = async (sessionId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/auth/session-data`, {
        headers: { 'X-Session-ID': sessionId }
      });
      
      const { session_token, ...userData } = response.data;
      localStorage.setItem('session_token', session_token);
      setSessionToken(session_token);
      setUser(userData);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('OAuth callback error:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('session_token');
      setSessionToken(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const token = response.data.access_token;
    localStorage.setItem('session_token', token);
    setSessionToken(token);
    await checkAuth();
  };

  const signup = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, { name, email, password });
    const token = response.data.access_token;
    localStorage.setItem('session_token', token);
    setSessionToken(token);
    await checkAuth();
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setSessionToken(null);
    setUser(null);
  };

  const loginWithGoogle = () => {
    const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `${AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, loginWithGoogle, sessionToken, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
